// Mobile Trade — a single review-first workspace (design: "Rebuilding the Trade Flow"). You land
// straight on the deal: a "You give" box and a "Target team → You get" card, both filled in place
// via bottom sheets. No target? "Shop this trade" runs your package past every team and returns
// real, salary-legal offers (getTradingBlockOffers). A deal that won't clear gets "Make it work"
// (tradeCounterOffer). Everything runs on ZenGM's real trade engine (updateTrade / proposeTrade /
// belovedness+morale+jobSecurity AI). A completed deal lands on a "You received / They received"
// recap.

import { useState } from "react";
import useTitleBar from "../hooks/useTitleBar.tsx";
import { useLocal } from "../util/local.ts";
import { useNoTradingAllowed } from "../util/noTradingAllowed.ts";
import { untradableMessage } from "../util/untradable.ts";
import { toWorker } from "../util/toWorker.ts";
import { RosterTabs } from "../components/RosterTabs.tsx";
import { TeamLogoInline } from "../components/TeamLogoInline.tsx";
import { MobileNotice } from "../components/MobileNotice.tsx";
import { PlayerTable } from "../components/PlayerTable.tsx";
import { SKILLS, orderSkills } from "../../common/constants.ts";
import type { View } from "../../common/types.ts";

const GREEN = "#18E0A8";
const BLUE = "#4A9EFF";
const money = (m: number) => `$${Math.round(m)}M`;
// Draft-pick descs from the worker embed <a> tags; strip them for these plain chips.
const pickName = (desc?: string) => (desc ?? "").replace(/<[^>]*>/g, "");
const lastName = (p: any) => p.lastName ?? p.name ?? "Player";
// A rough, clearly-estimated win impact from the team-ovr change the trade summary reports.
const WIN_PER_OVR = 2;

// Position accent colors (shared with the GmPlayer card treatment).
const POS_COLORS: Record<string, string> = {
	PG: "#4A9EFF",
	SG: "#2a7de1",
	SF: "#18E0A8",
	PF: "#E8B923",
	C: "#E2504E",
	GF: "#7C9FF0",
	FC: "#E8A020",
	F: "#9a6cff",
	G: "#4A9EFF",
};
const posColor = (p?: string) => POS_COLORS[p ?? ""] ?? "#4A9EFF";

// Shop offers with no "looking for" preference — any salary-legal return.
const ANY_LOOKING_FOR = {
	positions: {},
	skills: {},
	assets: { draftPicks: false, prospects: false, bestCurrentPlayers: false },
};

// "What are you after?" — each objective re-shops with a different target and re-ranks the offers.
const OBJECTIVES = [
	{
		key: "winNow",
		label: "Win Now",
		lookingFor: {
			positions: {},
			skills: {},
			assets: { draftPicks: false, prospects: false, bestCurrentPlayers: true },
		},
	},
	{
		key: "assets",
		label: "Get Assets",
		lookingFor: {
			positions: {},
			skills: {},
			assets: { draftPicks: true, prospects: true, bestCurrentPlayers: false },
		},
	},
	{
		key: "relief",
		label: "Financial Relief",
		lookingFor: ANY_LOOKING_FOR,
	},
] as const;

// ---- reusable "player treatment": pos badge + AGE/OVR/POT/PER tiles + skill badges ----
const StatTile = ({
	label,
	value,
	green,
}: {
	label: string;
	value: any;
	green?: boolean;
}) => (
	<div className="gmt-tile">
		<div className="gmt-tile-l">{label}</div>
		<div className="gmt-tile-v" style={green ? { color: GREEN } : undefined}>
			{value}
		</div>
	</div>
);

const DealPlayer = ({ p }: { p: any }) => {
	const r = p.ratings ?? {};
	const per = p.stats?.per;
	const skills: string[] = r.skills ?? [];
	return (
		<div className="gmt-dp">
			<div className="gmt-dp-top">
				<span className="gmt-dp-pos" style={{ background: posColor(r.pos) }}>
					{r.pos}
				</span>
				<span className="gmt-dp-name">
					{p.firstNameShort ?? p.firstName} {p.lastName}
				</span>
			</div>
			<div className="gmt-dp-tiles">
				<StatTile label="AGE" value={p.age ?? "—"} />
				<StatTile label="OVR" value={r.ovr ?? "—"} green />
				<StatTile label="POT" value={r.pot ?? "—"} />
				<StatTile
					label="PER"
					value={
						typeof per === "number" && per !== 0 ? per.toFixed(1) : "—"
					}
				/>
			</div>
			{skills.length ? (
				<div className="gmt-dp-badges">
					{orderSkills(skills)
						.slice(0, 4)
						.map((k) => (
							<span className="gmt-dp-badge" key={k} title={SKILLS[k] ?? k}>
								{k}
							</span>
						))}
				</div>
			) : null}
		</div>
	);
};

const DealPick = ({ p }: { p: any }) => (
	<div className="gmt-dpick">
		<span className="gmt-dpick-tag">PK</span>
		<span className="gmt-dpick-name">{pickName(p.desc)}</span>
		{p.recordLabel ? (
			<span className="gmt-dpick-rec">{p.recordLabel}</span>
		) : null}
	</div>
);

// Render a mixed list of players + picks as treatment cards.
const DealCards = ({ players, picks }: { players: any[]; picks: any[] }) => (
	<div className="gmt-deal-cards">
		{players.map((p, i) => (
			<DealPlayer key={`p${i}`} p={p} />
		))}
		{picks.map((p, i) => (
			<DealPick key={`k${i}`} p={p} />
		))}
	</div>
);

const GmTrade = (props: View<"trade">) => {
	useTitleBar({ title: "Trade" });
	const { userTid, lid, teamInfoCache, season } = useLocal([
		"userTid",
		"lid",
		"teamInfoCache",
		"season",
	]);
	const noTradingAllowed = useNoTradingAllowed(
		(props as any).otherTeamsWantToHire,
	);

	const {
		userRoster,
		userPicks,
		userPids,
		userDpids,
		userPidsExcluded,
		userDpidsExcluded,
		otherRoster,
		otherPicks,
		otherPids,
		otherDpids,
		otherPidsExcluded,
		otherDpidsExcluded,
		otherTid,
		teams,
		summary,
		won,
		lost,
	} = props as any;

	// Whether the user has explicitly committed to a target team (vs. the "any team — shop it"
	// empty state). The backend always holds a default partner, so we track intent in the UI.
	const [targetChosen, setTargetChosen] = useState<boolean>(
		() => otherPids.length > 0 || otherDpids.length > 0,
	);
	const [sheet, setSheet] = useState<null | "give" | "get" | "teams">(null);
	const [view, setView] = useState<"build" | "offers">("build");
	const [result, setResult] = useState<{ ok: boolean; msg: string } | null>(
		null,
	);
	const [offers, setOffers] = useState<any[] | null>(null);
	const [objective, setObjective] = useState<string>("winNow");
	const [busy, setBusy] = useState<null | "propose" | "shop" | "work">(null);
	const [madeMsg, setMadeMsg] = useState<string | null>(null);
	const [notice, setNotice] = useState<string | null>(null);
	const [noticeTitle, setNoticeTitle] = useState<string>("Can't trade this player");
	const [noticeVariant, setNoticeVariant] = useState<"error" | "success">(
		"error",
	);
	// A deal that came straight from a league offer is one the AI already floated — mark it as
	// "Likely accepted" until the user edits either side.
	const [dealFromOffer, setDealFromOffer] = useState<boolean>(false);

	const teamInfo = (tid: number) => (teamInfoCache as any[])[tid] ?? {};
	const targetTeam = teams.find((t: any) => t.tid === otherTid);

	// --- trade mutation (all via the real engine) ---
	const sync = (t0: any, t1: any) => {
		toWorker("main", "updateTrade", [
			{
				tid: userTid,
				pids: userPids,
				dpids: userDpids,
				pidsExcluded: userPidsExcluded,
				dpidsExcluded: userDpidsExcluded,
				...t0,
			},
			{
				tid: otherTid,
				pids: otherPids,
				dpids: otherDpids,
				pidsExcluded: otherPidsExcluded,
				dpidsExcluded: otherDpidsExcluded,
				...t1,
			},
		] as any);
		setResult(null);
		setMadeMsg(null);
		setDealFromOffer(false);
	};
	const tog = (arr: number[], id: number) =>
		arr.includes(id) ? arr.filter((x) => x !== id) : [...arr, id];
	const toggleGivePlayer = (pid: number) =>
		sync({ pids: tog(userPids, pid) }, {});
	const toggleGivePick = (dpid: number) =>
		sync({ dpids: tog(userDpids, dpid) }, {});
	const toggleGetPlayer = (pid: number) =>
		sync({}, { pids: tog(otherPids, pid) });
	const toggleGetPick = (dpid: number) =>
		sync({}, { dpids: tog(otherDpids, dpid) });

	const pickTeam = (tid: number) => {
		toWorker("main", "updateTrade", [
			{
				tid: userTid,
				pids: userPids,
				dpids: userDpids,
				pidsExcluded: userPidsExcluded,
				dpidsExcluded: userDpidsExcluded,
			},
			{ tid, pids: [], dpids: [], pidsExcluded: [], dpidsExcluded: [] },
		] as any);
		setTargetChosen(true);
		setResult(null);
		setMadeMsg(null);
		setDealFromOffer(false);
		setSheet(null);
	};
	const clearTeam = () => {
		sync({}, { pids: [], dpids: [] });
		setTargetChosen(false);
	};

	// Shop the package around the league for a given objective (defaults to the current one).
	const shop = async (
		obj: (typeof OBJECTIVES)[number] = OBJECTIVES.find(
			(o) => o.key === objective,
		) ?? OBJECTIVES[0],
	) => {
		setBusy("shop");
		setObjective(obj.key);
		const res = (await toWorker("main", "getTradingBlockOffers", {
			pids: userPids,
			dpids: userDpids,
			lookingFor: obj.lookingFor,
		} as any)) as any[];
		setBusy(null);
		setOffers(res ?? []);
		setView("offers");
	};

	const reviewOffer = (offer: any) => {
		toWorker("main", "updateTrade", [
			{
				tid: userTid,
				pids: offer.pidsUser,
				dpids: offer.dpidsUser,
				pidsExcluded: [],
				dpidsExcluded: [],
			},
			{
				tid: offer.tid,
				pids: offer.pids,
				dpids: offer.dpids,
				pidsExcluded: [],
				dpidsExcluded: [],
			},
		] as any);
		setTargetChosen(true);
		setView("build");
		setResult(null);
		setMadeMsg(null);
		setDealFromOffer(true);
	};

	const makeItWork = async () => {
		setBusy("work");
		const res = (await toWorker("main", "tradeCounterOffer", undefined)) as
			| { changed: boolean; message: string }
			| undefined;
		setBusy(null);
		setMadeMsg(res?.message || "Adjusted the deal to get it over the line.");
		setResult(null);
	};

	const propose = async () => {
		setBusy("propose");
		const teamName = targetTeam
			? `${targetTeam.region} ${targetTeam.name}`
			: "the other team";
		const res: any = await toWorker("main", "proposeTrade", false);
		setBusy(null);
		const ok = !!res?.[0];
		if (ok) {
			// Deal is done — wipe the workspace and drop back to the empty state to build the next one.
			await toWorker("main", "clearTrade", "all");
			setTargetChosen(false);
			setDealFromOffer(false);
			setView("build");
			setResult(null);
			setMadeMsg(null);
			setOffers(null);
			setNoticeTitle("Trade complete");
			setNoticeVariant("success");
			setNotice(res?.[1] || `Your trade with ${teamName} went through.`);
		} else {
			setResult({ ok, msg: res?.[1] ?? "" });
		}
	};

	// --- derived deal state ---
	const giveP = userRoster.filter((p: any) => p.included);
	const givePk = userPicks.filter((p: any) => p.included);
	const give = [...giveP, ...givePk];
	const getP = otherRoster.filter((p: any) => p.included);
	const getPk = otherPicks.filter((p: any) => p.included);
	const get = [...getP, ...getPk];
	const salaryOut = giveP.reduce(
		(s: number, p: any) => s + p.contract.amount,
		0,
	);
	const salaryIn = getP.reduce((s: number, p: any) => s + p.contract.amount, 0);
	// summary's ovr fields are cross-indexed: teams[1] holds YOUR before/after ovr and
	// teams[0] the target team's (same mapping the desktop TradingBlock view uses).
	const winImpact = (i: 0 | 1) => {
		const t = summary.teams[i];
		return {
			before: t.ovrBefore,
			after: t.ovrAfter,
			wins: Math.round((t.ovrAfter - t.ovrBefore) * WIN_PER_OVR * 10) / 10,
		};
	};
	const youImpact = winImpact(1);
	const themImpact = winImpact(0);
	const hasGive = give.length > 0;
	const salaryOk = !summary.warning;

	// ============================= sub-renders =============================
	const TeamCrest = ({ tid, size = 28 }: { tid: number; size?: number }) => {
		const t = teamInfo(tid);
		return (
			<TeamLogoInline
				imgURL={t.imgURL}
				imgURLSmall={t.imgURLSmall}
				size={size}
			/>
		);
	};

	const SheetRow = ({ p, on, onClick, pick }: any) => (
		<div className="gmt-srow">
			<div className="gmt-sinfo">
				<div className="gmt-sname">
					{pick
						? pickName(p.desc)
						: `${p.firstNameShort ?? p.firstName} ${p.lastName}`}
				</div>
				<div className="gmt-smeta">
					{pick
						? "Draft pick"
						: `${p.ratings.pos} · OVR ${p.ratings.ovr} · ${money(p.contract.amount)}`}
				</div>
			</div>
			<button
				className={`gmt-add ${on ? "on" : ""}`}
				onClick={onClick}
				disabled={p.untradable}
			>
				{on ? "✓ Added" : "Add"}
			</button>
		</div>
	);

	// ============================= OFFERS (shop the league) =============================
	if (view === "offers") {
		// Rank offers by the chosen objective: best players (Win Now), most/best assets (Get
		// Assets), or least salary taken back (Financial Relief).
		const salaryInOf = (o: any) =>
			o.players.reduce((s: number, p: any) => s + (p.contract?.amount ?? 0), 0);
		const ovrOf = (o: any) =>
			o.players.reduce((s: number, p: any) => s + (p.ratings?.ovr ?? 0), 0);
		const potOf = (o: any) =>
			o.players.reduce((s: number, p: any) => s + (p.ratings?.pot ?? 0), 0);
		const ranked = [...(offers ?? [])].sort((a, b) => {
			if (objective === "relief") return salaryInOf(a) - salaryInOf(b);
			if (objective === "assets")
				return b.picks.length - a.picks.length || potOf(b) - potOf(a);
			return ovrOf(b) - ovrOf(a);
		});
		const labelFor = (offer: any) => {
			// Honest label from how the deal nets out for the user (summary total, higher = better).
			const t = offer.summary?.teams?.[0]?.total ?? 0;
			if (t > 5) return { text: "Great return", color: GREEN };
			if (t >= -3) return { text: "Fair value", color: BLUE };
			return { text: "They'll want more", color: "#E8B923" };
		};
		const giveLabel =
			give
				.map((p: any) => (p.desc ? pickName(p.desc) : lastName(p)))
				.join(", ") || "your package";
		return (
			<div className="gmt">
				<style>{styles}</style>
				<RosterTabs active="trade" lid={lid} />
				<div className="gmt-offers-head">
					<div>
						<div className="gmt-box-h" style={{ color: GREEN }}>
							OFFERS AROUND THE LEAGUE
						</div>
						<div className="gmt-offers-sub">
							Teams that would take {giveLabel} — pick the best return.
						</div>
					</div>
					<button className="gmt-back" onClick={() => setView("build")}>
						Back
					</button>
				</div>
				<div className="gmt-obj-lab">WHAT ARE YOU AFTER?</div>
				<div className="gmt-obj-chips">
					{OBJECTIVES.map((o) => (
						<button
							key={o.key}
							className={`gmt-obj ${objective === o.key ? "on" : ""}`}
							disabled={busy !== null}
							onClick={() => shop(o)}
						>
							{o.label}
						</button>
					))}
				</div>
				{busy === "shop" ? (
					<div className="gmt-empty-note">Shopping the league…</div>
				) : ranked.length === 0 ? (
					<div className="gmt-empty-note">
						No team will take that package right now. Add more value or try
						different assets.
					</div>
				) : (
					ranked.map((offer: any) => {
						const label = labelFor(offer);
						const theirs = [...offer.players, ...offer.picks];
						return (
							<div className="gmt-offer" key={offer.tid}>
								<div className="gmt-offer-top">
									<TeamCrest tid={offer.tid} size={30} />
									<div className="gmt-offer-team">
										<div className="gmt-offer-name">
											{teamInfo(offer.tid).region ??
												teamInfo(offer.tid).name ??
												`Team ${offer.tid}`}
										</div>
										<div className="gmt-offer-rec">
											{offer.won}-{offer.lost}
										</div>
									</div>
									<span
										className="gmt-offer-label"
										style={{ color: label.color, borderColor: label.color }}
									>
										{label.text}
									</span>
								</div>
								<div className="gmt-offer-lab">THEY SEND YOU</div>
								{theirs.length === 0 ? (
									<div className="gmt-chips">
										<span className="gmt-chip">—</span>
									</div>
								) : (
									<DealCards players={offer.players} picks={offer.picks} />
								)}
								<button
									className="gmt-review"
									onClick={() => reviewOffer(offer)}
								>
									Review this trade
								</button>
							</div>
						);
					})
				)}
			</div>
		);
	}

	// ============================= BUILD (workspace) =============================
	if (noTradingAllowed) {
		return (
			<div className="gmt">
				<style>{styles}</style>
				<RosterTabs active="trade" lid={lid} />
				<div className="gmt-box">
					<div className="gmt-box-h" style={{ color: GREEN }}>
						TRADES ARE CLOSED
					</div>
					<div className="gmt-box-empty">
						You can't propose or shop trades right now. Trading reopens outside
						the playoffs and trade-deadline window.
					</div>
				</div>
			</div>
		);
	}

	return (
		<div className="gmt">
			<style>{styles}</style>
			<RosterTabs active="trade" lid={lid} />

			{/* YOU GIVE */}
			<div
				className="gmt-box gmt-box-give"
				onClick={() => setSheet("give")}
				role="button"
			>
				<div className="gmt-box-top">
					<div className="gmt-box-h" style={{ color: GREEN }}>
						YOU GIVE
					</div>
					<span className="gmt-box-edit">{hasGive ? "Edit" : "Add +"}</span>
				</div>
				{hasGive ? (
					<>
						<DealCards players={giveP} picks={givePk} />
						<div className="gmt-box-sal">Salary out · {money(salaryOut)}</div>
					</>
				) : (
					<div className="gmt-box-empty">Tap to add your players and picks</div>
				)}
			</div>

			{/* TARGET TEAM + YOU GET */}
			{!targetChosen ? (
				<div
					className="gmt-box gmt-box-target"
					onClick={() => setSheet("teams")}
					role="button"
				>
					<div className="gmt-target-empty">
						<div className="gmt-qmark">?</div>
						<div>
							<div className="gmt-box-h" style={{ color: BLUE }}>
								TARGET TEAM
							</div>
							<div className="gmt-box-empty" style={{ margin: 0 }}>
								Any team — shop it around
							</div>
						</div>
						<span className="gmt-chevron">›</span>
					</div>
				</div>
			) : (
				<div className="gmt-box gmt-box-target">
					<div
						className="gmt-target-row"
						onClick={() => setSheet("teams")}
						role="button"
					>
						<TeamCrest tid={otherTid} size={30} />
						<div className="gmt-target-info">
							<div className="gmt-box-h" style={{ color: BLUE, margin: 0 }}>
								TARGET TEAM
							</div>
							<div className="gmt-target-name">
								{targetTeam ? targetTeam.region : ""}{" "}
								{won !== undefined ? `· ${won}-${lost}` : ""}
							</div>
						</div>
						<span className="gmt-box-edit">Change</span>
					</div>
					<div
						className="gmt-you-get"
						onClick={() => setSheet("get")}
						role="button"
					>
						<div className="gmt-box-top">
							<div className="gmt-box-h" style={{ color: BLUE }}>
								YOU GET
							</div>
							<span className="gmt-box-edit">
								{get.length ? "Edit" : "Add +"}
							</span>
						</div>
						{get.length ? (
							<>
								<DealCards players={getP} picks={getPk} />
								<div className="gmt-box-sal">Salary in · {money(salaryIn)}</div>
							</>
						) : (
							<div className="gmt-box-empty">
								Tap to add their players and picks
							</div>
						)}
					</div>
					<button className="gmt-clear" onClick={clearTeam}>
						Clear team
					</button>
				</div>
			)}

			{/* summary rows (only once there's something to judge) */}
			{hasGive && (targetChosen ? get.length > 0 : false) ? (
				<>
					<div className="gmt-sumrow">
						<div>
							<div className="gmt-sumlab">Salary match</div>
							<div className="gmt-sumsub">
								{summary.warning ?? "Legal under cap rules"}
							</div>
						</div>
						<div
							className="gmt-sumval"
							style={{ color: salaryOk ? GREEN : "#E2504E" }}
						>
							{salaryOk ? "✓" : "✕"}
						</div>
					</div>
					<div className="gmt-sumrow">
						<div style={{ width: "100%" }}>
							<div className="gmt-sumlab">Win impact</div>
							<div className="gmt-winsides">
								{(
									[
										{ tag: "YOU", imp: youImpact },
										{
											tag: teamInfo(otherTid).abbrev ?? "THEM",
											imp: themImpact,
										},
									] as const
								).map(({ tag, imp }) => (
									<div className="gmt-winside" key={tag}>
										<div className="gmt-winteam">{tag}</div>
										<div className="gmt-sumsub">
											OVR {imp.before} → {imp.after}
										</div>
										<div
											className="gmt-sumval"
											style={{ color: imp.wins >= 0 ? GREEN : "#E2504E" }}
										>
											{imp.wins >= 0 ? "+" : ""}
											{imp.wins} wins
										</div>
									</div>
								))}
							</div>
						</div>
					</div>
				</>
			) : null}

			{/* MAKE IT WORK — shown whenever a built deal is rejected OR can't clear (salary/value). */}
			{(() => {
				const bothSides = hasGive && get.length > 0;
				const rejected = !!result && !result.ok;
				const blocked = bothSides && !summary.enablePropose;
				if (!madeMsg && !rejected && !blocked) return null;
				return (
					<div className="gmt-work">
						<div className="gmt-work-h">
							{madeMsg
								? "MAKE IT WORK"
								: rejected
									? "THEY REJECTED"
									: "ALMOST THERE"}
						</div>
						<div className="gmt-work-msg">
							{madeMsg
								? madeMsg
								: rejected
									? result!.msg || "This deal doesn't work for them yet."
									: summary.warning ||
										"This deal doesn't satisfy the trade rules yet."}
						</div>
						{!madeMsg ? (
							<div className="gmt-work-actions">
								<button
									className="gmt-work-btn"
									disabled={busy !== null}
									onClick={makeItWork}
								>
									{busy === "work" ? "Working…" : "Make it work"}
								</button>
								<button
									className="gmt-work-btn ghost"
									disabled={busy !== null || !hasGive}
									onClick={() => shop()}
								>
									Shop it →
								</button>
							</div>
						) : null}
					</div>
				);
			})()}

			{/* primary action */}
			{targetChosen ? (
				<>
					{dealFromOffer && hasGive && get.length > 0 ? (
						<div className="gmt-accept">Likely accepted</div>
					) : null}
					<div className="gmt-footer">
						<button
							className="gmt-back-btn"
							disabled={busy !== null}
							onClick={() => (offers ? setView("offers") : clearTeam())}
						>
							Back
						</button>
						<button
							className="gmt-cta"
							disabled={!summary.enablePropose || busy !== null}
							onClick={propose}
						>
							{busy === "propose" ? "Sending…" : "Send trade offer"}
						</button>
					</div>
				</>
			) : (
				<button
					className="gmt-cta"
					disabled={!hasGive || busy !== null}
					onClick={() => shop()}
				>
					{busy === "shop" ? "Shopping the league…" : "Shop this trade"}
				</button>
			)}

			{/* ===================== bottom sheets ===================== */}
			{sheet ? (
				<div className="gmt-sheet-backdrop" onClick={() => setSheet(null)}>
					<div className="gmt-sheet" onClick={(e) => e.stopPropagation()}>
						<div className="gmt-grab" />
						<div className="gmt-sheet-head">
							<div className="gmt-sheet-title">
								{sheet === "give"
									? "Add what you give"
									: sheet === "get"
										? `Get from ${targetTeam ? targetTeam.name : "them"}`
										: "Pick a team"}
							</div>
							<button className="gmt-done-btn" onClick={() => setSheet(null)}>
								Done
							</button>
						</div>
						<div className="gmt-sheet-body">
							{sheet === "teams" ? (
								[...teams]
									.filter((t: any) => t.tid !== userTid)
									.sort((a: any, b: any) =>
										(a.region ?? "").localeCompare(b.region ?? ""),
									)
									.map((t: any) => (
										<button
											key={t.tid}
											className="gmt-teamrow"
											onClick={() => pickTeam(t.tid)}
										>
											<TeamCrest tid={t.tid} size={26} />
											<span>
												{t.region} {t.name}
											</span>
											<span className="gmt-chevron">›</span>
										</button>
									))
							) : sheet === "give" ? (
								<>
									<div className="gmt-sec">YOUR PLAYERS</div>
									<PlayerTable
										players={userRoster}
										season={season}
										onName={(p) => {
											const msg = untradableMessage(p);
											if (msg) {
												setNoticeTitle("Can't trade this player");
												setNoticeVariant("error");
												setNotice(msg);
												return;
											}
											toggleGivePlayer(p.pid);
										}}
										action={(p) => (
											<button
												className={`pt-actbtn ${p.included ? "on" : ""}`}
												disabled={p.untradable}
												onClick={() => toggleGivePlayer(p.pid)}
											>
												{p.included ? "✓" : "Add"}
											</button>
										)}
									/>
									<div className="gmt-sec">YOUR DRAFT PICKS</div>
									{userPicks.map((dp: any) => (
										<SheetRow
											key={dp.dpid}
											p={dp}
											pick
											on={dp.included}
											onClick={() => toggleGivePick(dp.dpid)}
										/>
									))}
								</>
							) : (
								<>
									<div className="gmt-sec">
										{targetTeam ? targetTeam.name.toUpperCase() : "THEIR"}{" "}
										PLAYERS
									</div>
									<PlayerTable
										players={otherRoster}
										season={season}
										onName={(p) => {
											const msg = untradableMessage(p);
											if (msg) {
												setNoticeTitle("Can't trade this player");
												setNoticeVariant("error");
												setNotice(msg);
												return;
											}
											toggleGetPlayer(p.pid);
										}}
										action={(p) => (
											<button
												className={`pt-actbtn ${p.included ? "on" : ""}`}
												disabled={p.untradable}
												onClick={() => toggleGetPlayer(p.pid)}
											>
												{p.included ? "✓" : "Add"}
											</button>
										)}
									/>
									<div className="gmt-sec">THEIR DRAFT PICKS</div>
									{otherPicks.map((dp: any) => (
										<SheetRow
											key={dp.dpid}
											p={dp}
											pick
											on={dp.included}
											onClick={() => toggleGetPick(dp.dpid)}
										/>
									))}
								</>
							)}
						</div>
					</div>
				</div>
			) : null}
			<MobileNotice
				message={notice}
				title={noticeTitle}
				variant={noticeVariant}
				onClose={() => setNotice(null)}
			/>
		</div>
	);
};

const styles = `
.gmt { max-width:760px; margin:0 auto; padding:14px 16px 48px; color:#fff; font-family:Montserrat,system-ui,sans-serif; }
.gmt-box { background:#181818; border:1px solid #232323; border-radius:16px; padding:14px; margin-bottom:12px; cursor:pointer; }
.gmt-box-give { border-color:rgba(24,224,168,.35); background:linear-gradient(180deg,rgba(24,224,168,.06),#181818); }
.gmt-box-target { cursor:default; border-color:rgba(74,158,255,.3); }
.gmt-box-top { display:flex; align-items:center; justify-content:space-between; }
.gmt-box-h { font-size:10px; font-weight:900; letter-spacing:.7px; margin-bottom:8px; }
.gmt-box-edit { font-size:12px; font-weight:800; color:#7f8b93; }
.gmt-box-empty { color:#7f8b93; font-size:13px; font-weight:600; margin-top:4px; }
.gmt-box-sal { font-size:11px; font-weight:700; color:#8fb3c9; margin-top:10px; }
.gmt-chips { display:flex; flex-wrap:wrap; gap:6px; margin-top:2px; }
.gmt-chip { background:#232323; border:1px solid #2f2f2f; border-radius:8px; padding:5px 9px; font-size:12px; font-weight:800; }

/* player / pick treatment cards */
.gmt-deal-cards { margin-top:4px; }
.gmt-dp { background:#141414; border:1px solid #232323; border-radius:12px; padding:11px; margin-bottom:8px; }
.gmt-dp-top { display:flex; align-items:center; gap:9px; margin-bottom:9px; }
.gmt-dp-pos { width:30px; height:30px; border-radius:8px; color:#0a0a0a; font-weight:900; font-size:12px;
  display:flex; align-items:center; justify-content:center; flex:none; }
.gmt-dp-name { font-weight:800; font-size:15px; }
.gmt-dp-tiles { display:flex; gap:6px; }
.gmt-tile { flex:1; background:#101010; border:1px solid #202020; border-radius:8px; padding:6px 4px; text-align:center; }
.gmt-tile-l { font-size:8.5px; font-weight:900; letter-spacing:.5px; color:#7f8b93; }
.gmt-tile-v { font-size:15px; font-weight:900; margin-top:2px; }
.gmt-dp-badges { display:flex; flex-wrap:wrap; gap:4px; margin-top:9px; }
.gmt-dp-badge { height:16px; padding:0 5px; border-radius:5px; background:#17251f; color:#18E0A8; font-size:9px; font-weight:900;
  display:flex; align-items:center; }
.gmt-dpick { display:flex; align-items:center; gap:9px; background:#141414; border:1px solid #232323; border-radius:12px;
  padding:12px 11px; margin-bottom:8px; }
.gmt-dpick-tag { width:30px; height:30px; border-radius:8px; background:#2a2a2a; color:#cdd4da; font-weight:900; font-size:10px;
  display:flex; align-items:center; justify-content:center; flex:none; }
.gmt-dpick-name { font-weight:800; font-size:14px; flex:1; min-width:0; }
.gmt-dpick-rec { font-size:10px; font-weight:800; color:#8fb3c9; background:#1b2733; border:1px solid #2a3a48;
  border-radius:6px; padding:3px 7px; flex:none; }

/* offers objective chips */
.gmt-obj-lab { font-size:10px; font-weight:900; letter-spacing:.6px; color:#7f8b93; margin:2px 2px 8px; }
.gmt-obj-chips { display:flex; gap:8px; margin-bottom:14px; flex-wrap:wrap; }
.gmt-obj { border:1px solid #2f2f2f; background:#181818; color:#cdd4da; font-weight:800; font-size:13px; padding:9px 15px;
  border-radius:20px; cursor:pointer; font-family:inherit; }
.gmt-obj.on { background:#4A9EFF; border-color:#4A9EFF; color:#06213f; }
.gmt-obj:disabled { opacity:.5; }

/* review footer */
.gmt-accept { color:#18E0A8; font-weight:900; font-size:13px; margin:2px 2px 8px; }
.gmt-footer { display:flex; gap:10px; align-items:stretch; margin-top:10px; }
.gmt-footer .gmt-cta { margin-top:0; flex:1; }
.gmt-back-btn { border:none; background:#232323; color:#cdd4da; font-weight:800; font-size:14px; padding:0 22px;
  border-radius:26px; cursor:pointer; font-family:inherit; flex:none; }
.gmt-back-btn:disabled { opacity:.5; }

.gmt-target-empty { display:flex; align-items:center; gap:11px; }
.gmt-qmark { width:34px; height:34px; border-radius:10px; background:#22405a; color:#9fc6ef; font-weight:900;
  display:flex; align-items:center; justify-content:center; flex:none; }
.gmt-target-row { display:flex; align-items:center; gap:11px; cursor:pointer; }
.gmt-target-info { flex:1; min-width:0; }
.gmt-target-name { font-size:14px; font-weight:800; margin-top:2px; }
.gmt-chevron { color:#7f8b93; font-size:20px; margin-left:auto; }
.gmt-you-get { margin-top:14px; padding-top:14px; border-top:1px solid #232323; cursor:pointer; }
.gmt-clear { background:none; border:none; color:#E2504E; font-weight:700; font-size:12px; cursor:pointer; margin-top:12px; padding:0; font-family:inherit; }

.gmt-sumrow { display:flex; justify-content:space-between; align-items:center; background:#151515; border:1px solid #232323;
  border-radius:12px; padding:12px 14px; margin-bottom:8px; }
.gmt-sumlab { font-weight:800; font-size:14px; }
.gmt-sumsub { font-size:11px; color:#8fb3c9; margin-top:1px; }
.gmt-sumval { font-weight:900; font-size:17px; }
.gmt-winsides { display:flex; gap:8px; margin-top:8px; }
.gmt-winside { flex:1; background:#101010; border:1px solid #232323; border-radius:10px; padding:8px 10px; }
.gmt-winteam { font-size:10px; font-weight:900; letter-spacing:.6px; color:#7f8b93; margin-bottom:2px; }

.gmt-work { background:linear-gradient(180deg,rgba(232,185,35,.1),#181818); border:1px solid rgba(232,185,35,.4);
  border-radius:14px; padding:13px; margin-bottom:12px; }
.gmt-work-h { font-size:10px; font-weight:900; letter-spacing:.6px; color:#E8B923; margin-bottom:5px; }
.gmt-work-msg { font-size:13px; font-weight:600; color:#e6dcc2; line-height:1.4; }
.gmt-work-actions { display:flex; gap:8px; margin-top:11px; }
.gmt-work-btn { flex:1; border:none; border-radius:20px; background:#E8B923; color:#231c05; font-weight:800; font-size:13px; padding:10px; cursor:pointer; font-family:inherit; }
.gmt-work-btn.ghost { background:none; border:1px solid #3a3a3a; color:#cdd4da; }
.gmt-work-btn:disabled { opacity:.5; }

.gmt-cta { display:block; width:100%; margin-top:10px;
  border:none; border-radius:26px; background:#18E0A8; color:#0a0a0a; font-weight:900; font-size:15px; padding:15px;
  cursor:pointer; font-family:inherit; box-shadow:0 10px 26px rgba(24,224,168,.3); }
.gmt-cta:disabled { background:#2a2a2a; color:#7f8b93; box-shadow:none; }

/* offers */
.gmt-offers-head { display:flex; justify-content:space-between; align-items:flex-start; margin:4px 2px 12px; }
.gmt-offers-sub { font-size:12px; color:#8fb3c9; font-weight:600; margin-top:3px; max-width:80%; }
.gmt-back { background:none; border:none; color:#4A9EFF; font-weight:800; font-size:13px; cursor:pointer; font-family:inherit; }
.gmt-empty-note { color:#8a8a8a; font-size:13px; text-align:center; padding:30px 16px; }
.gmt-offer { background:#181818; border:1px solid #232323; border-radius:14px; padding:13px; margin-bottom:11px; }
.gmt-offer-top { display:flex; align-items:center; gap:10px; }
.gmt-offer-team { flex:1; min-width:0; }
.gmt-offer-name { font-weight:800; font-size:14px; }
.gmt-offer-rec { font-size:11px; color:#7f8b93; }
.gmt-offer-label { font-size:11px; font-weight:800; border:1px solid; border-radius:20px; padding:3px 9px; }
.gmt-offer-lab { font-size:9px; font-weight:900; letter-spacing:.6px; color:#7f8b93; margin:11px 0 7px; }
.gmt-review { width:100%; border:none; border-radius:20px; background:#18E0A8; color:#0a0a0a; font-weight:800; font-size:13px; padding:11px; margin-top:11px; cursor:pointer; font-family:inherit; }

/* recap */
.gmt-done { text-align:center; padding:22px 0 16px; }
.gmt-done-check { width:56px; height:56px; border-radius:50%; background:#18E0A8; color:#053a2c; font-size:28px; font-weight:900;
  display:flex; align-items:center; justify-content:center; margin:0 auto 12px; }
.gmt-done-title { font-size:22px; font-weight:900; }
.gmt-done-sub { font-size:13px; color:#8fb3c9; margin-top:4px; }
.gmt-box-get { border-color:rgba(74,158,255,.3); background:linear-gradient(180deg,rgba(74,158,255,.06),#181818); cursor:default; }
.gmt-rrow { display:flex; justify-content:space-between; align-items:center; padding:7px 0; border-top:1px solid #202020; }
.gmt-rname { font-weight:800; font-size:14px; }
.gmt-rmeta { font-size:11px; color:#8fb3c9; }

/* bottom sheet */
.gmt-sheet-backdrop { position:fixed; inset:0; background:rgba(0,0,0,.55); z-index:200; display:flex; align-items:flex-end; }
.gmt-sheet { width:100%; max-width:760px; margin:0 auto; background:#141414; border-radius:20px 20px 0 0; border-top:1px solid #2a2a2a;
  max-height:80vh; display:flex; flex-direction:column; }
.gmt-grab { width:38px; height:4px; border-radius:2px; background:#3a3a3a; margin:9px auto 4px; }
.gmt-sheet-head { display:flex; align-items:center; justify-content:space-between; padding:6px 16px 10px; border-bottom:1px solid #232323; }
.gmt-sheet-title { font-weight:900; font-size:16px; }
.gmt-done-btn { background:none; border:none; color:#18E0A8; font-weight:800; font-size:15px; cursor:pointer; font-family:inherit; }
.gmt-sheet-body { overflow-y:auto; padding:8px 16px 24px; }
.gmt-sec { font-size:9px; letter-spacing:.6px; color:#7f8b93; font-weight:900; margin:12px 2px 8px; }
.gmt-srow { display:flex; align-items:center; gap:10px; padding:9px 0; border-bottom:1px solid #202020; }
.gmt-sinfo { flex:1; min-width:0; }
.gmt-sname { font-weight:800; font-size:14px; }
.gmt-smeta { font-size:11px; color:#8fb3c9; margin-top:1px; }
.gmt-add { border:1px solid #2f6b52; background:none; color:#18E0A8; font-weight:800; font-size:12px; padding:7px 14px; border-radius:16px; cursor:pointer; font-family:inherit; flex:none; }
.gmt-add.on { background:#18E0A8; color:#053a2c; border-color:#18E0A8; }
.gmt-add:disabled { opacity:.35; }
.gmt-teamrow { display:flex; align-items:center; gap:11px; width:100%; text-align:left; background:none; border:none; border-bottom:1px solid #202020;
  color:#fff; font-weight:700; font-size:14px; padding:12px 2px; cursor:pointer; font-family:inherit; }
`;

export default GmTrade;
